import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiUrl } from '../config/api';
import AppDateField from '../components/inputs/AppDateField';
import { formatSriLankaDateTimeLabel } from '../lib/sriLankaTime';

const API_URL = apiUrl('activity_logs');
const METHOD_OPTIONS = ['', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

function formatLogTime(value) {
  if (!value) return '—';
  return formatSriLankaDateTimeLabel(value);
}

function detailsPreview(details) {
  if (!details || typeof details !== 'object') return '—';

  const chunks = [];
  if (details.body && typeof details.body === 'object') {
    const bodyKeys = Object.keys(details.body).slice(0, 4);
    if (bodyKeys.length) chunks.push(`Body: ${bodyKeys.join(', ')}`);
  }
  if (details.query && typeof details.query === 'object') {
    const queryKeys = Object.keys(details.query).slice(0, 4);
    if (queryKeys.length) chunks.push(`Query: ${queryKeys.join(', ')}`);
  }
  if (details.params && typeof details.params === 'object') {
    const paramKeys = Object.keys(details.params).slice(0, 4);
    if (paramKeys.length) chunks.push(`Params: ${paramKeys.join(', ')}`);
  }
  if (details.meta?.duration_ms != null) {
    chunks.push(`Duration: ${details.meta.duration_ms} ms`);
  }

  return chunks.length ? chunks.join(' | ') : 'No extra details';
}

export default function ActivityLogsPage() {
  const [filters, setFilters] = useState({
    q: '',
    actor: '',
    method: '',
    date_from: '',
    date_to: '',
    page: 1,
  });
  const [payload, setPayload] = useState({
    items: [],
    pagination: { page: 1, total_pages: 1, total: 0, limit: 100 },
  });
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '100');
    params.set('page', String(filters.page || 1));
    if (filters.q) params.set('q', filters.q);
    if (filters.actor) params.set('actor', filters.actor);
    if (filters.method) params.set('method', filters.method);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    return params.toString();
  }, [filters]);

  useEffect(() => {
    let active = true;
    setLoading(true);

    fetch(`${API_URL}?${queryString}`)
      .then((r) => r.json())
      .then((data) => {
        if (!active) return;
        setPayload({
          items: Array.isArray(data?.items) ? data.items : [],
          pagination: data?.pagination || { page: 1, total_pages: 1, total: 0, limit: 100 },
        });
      })
      .catch(() => {
        if (!active) return;
        setPayload({
          items: [],
          pagination: { page: 1, total_pages: 1, total: 0, limit: 100 },
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [queryString]);

  const items = payload.items || [];
  const pagination = payload.pagination || { page: 1, total_pages: 1, total: 0, limit: 100 };

  const setField = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1,
    }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Activity Logs
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Append-only system activity history. These logs are view-only in the app and available to super admins only.
        </Typography>
      </Stack>

      <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
          <TextField
            label="Search"
            value={filters.q}
            onChange={(e) => setField('q', e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
          />
          <TextField
            label="Username"
            value={filters.actor}
            onChange={(e) => setField('actor', e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          />
          <TextField
            select
            label="Method"
            value={filters.method}
            onChange={(e) => setField('method', e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            {METHOD_OPTIONS.filter(Boolean).map((method) => (
              <MenuItem key={method} value={method}>{method}</MenuItem>
            ))}
          </TextField>
          <AppDateField
            label="From"
            value={filters.date_from}
            onChange={(e) => setField('date_from', e.target.value)}
            size="small"
            helperText="DD/MM/YYYY"
          />
          <AppDateField
            label="To"
            value={filters.date_to}
            onChange={(e) => setField('date_to', e.target.value)}
            size="small"
            helperText="DD/MM/YYYY"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setField('page', 1)}
          >
            Refresh
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
          <Chip label={`Total Logs: ${pagination.total || 0}`} color="primary" variant="outlined" />
          <Chip label={`Page ${pagination.page || 1} / ${pagination.total_pages || 1}`} variant="outlined" />
          <Chip label="Time zone: Asia/Colombo" variant="outlined" />
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6">No activity logs found</Typography>
            <Typography variant="body2" color="text.secondary">
              Try widening the date range or clearing the filters.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatLogTime(item.created_at)}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 700 }}>{item.actor_name || 'Unknown'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.actor_role || '—'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.request_method || '—'}
                      size="small"
                      color={item.request_method === 'GET' ? 'default' : 'primary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 600 }}>{item.action_name || item.request_path}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.request_path}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{item.branch_name || 'All Branches'}</TableCell>
                  <TableCell>
                    <Chip
                      label={`${item.outcome || 'success'} (${item.status_code || 0})`}
                      size="small"
                      color={String(item.outcome || '').toLowerCase() === 'error' ? 'error' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {detailsPreview(item.details)}
                      </Typography>
                      <Button size="small" variant="text" onClick={() => setSelectedLog(item)}>
                        View Full Log
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          disabled={loading || Number(pagination.page || 1) <= 1}
          onClick={() => setField('page', Math.max(1, Number(pagination.page || 1) - 1))}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary">
          Showing up to {pagination.limit || 100} logs per page
        </Typography>
        <Button
          variant="outlined"
          disabled={loading || Number(pagination.page || 1) >= Number(pagination.total_pages || 1)}
          onClick={() => setField('page', Number(pagination.page || 1) + 1)}
        >
          Next
        </Button>
      </Stack>

      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Activity Log Details</DialogTitle>
        <DialogContent dividers>
          {selectedLog ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                <Chip label={`User: ${selectedLog.actor_name || 'Unknown'}`} />
                <Chip label={`Role: ${selectedLog.actor_role || '—'}`} />
                <Chip label={`Method: ${selectedLog.request_method || '—'}`} />
                <Chip label={`Status: ${selectedLog.status_code || 0}`} />
                <Chip label={`Branch: ${selectedLog.branch_name || 'All Branches'}`} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {formatLogTime(selectedLog.created_at)}
              </Typography>
              <Typography variant="subtitle2">Action</Typography>
              <Typography>{selectedLog.action_name || '—'}</Typography>
              <Typography variant="subtitle2">Path</Typography>
              <Typography>{selectedLog.request_path || '—'}</Typography>
              <Typography variant="subtitle2">IP / Device</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedLog.ip_address || '—'}
                {selectedLog.user_agent ? ` | ${selectedLog.user_agent}` : ''}
              </Typography>
              <Typography variant="subtitle2">Details</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#0f172a', color: '#e2e8f0', overflowX: 'auto' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(selectedLog.details || {}, null, 2)}
                </pre>
              </Paper>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
